import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

/* ─── GENE'S EARTH TONE PALETTE ──────────────────── */
const C = {
  forest: '#2e7d32',
  forestDark: '#1b5e20',
  forestLight: '#c8e6c9',
  forestPale: '#e8f5e9',
  brownie: '#8b4513',
  brownieDk: '#6d3410',
  brownieLt: '#a0673e',
  browniePale: '#f5ede5',
  cream: '#faf6f1',
  creamDk: '#f0ebe3',
  creamDeep: '#e3dbd0',
  charcoal: '#36342f',
  charcoalLt: '#5c5a54',
  charcoalPale: '#7a776f',
  sage: '#87a96b',
  sagePale: '#d4e4c5',
  terra: '#c14242',
  white: '#ffffff',
};

/* ─── INTERSECTION OBSERVER HOOK ─────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

/* ─── FADE-IN WRAPPER ────────────────────────────── */
const FadeIn = ({ children, delay = 0, style = {} }) => {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'translateY(0)' : 'translateY(24px)',
      transition: `all 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
};

/* ─── HERO ───────────────────────────────────────── */
const Hero = () => {
  const [visible, setVisible] = useState(false);
  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  return (
    <section style={{
      minHeight: '90vh', background: C.cream, position: 'relative',
      overflow: 'hidden', display: 'flex', alignItems: 'center',
    }}>
      {/* Organic decorative shapes */}
      <div style={{ position: 'absolute', top: -60, right: -80, width: 300, height: 300, borderRadius: '60% 40% 50% 50%', opacity: 0.04, background: C.forest, transform: 'rotate(30deg)' }} />
      <div style={{ position: 'absolute', bottom: -40, left: -60, width: 300, height: 300, borderRadius: '60% 40% 50% 50%', opacity: 0.04, background: C.brownie, transform: 'rotate(-20deg)' }} />

      <div style={{
        maxWidth: 1200, margin: '0 auto', padding: '60px 24px 80px',
        display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 72,
        alignItems: 'center', position: 'relative', zIndex: 1,
      }}>
        {/* Left: copy */}
        <div style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(30px)',
          transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)',
        }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 16px', borderRadius: 100,
            background: C.forestPale, border: `1px solid ${C.forestLight}`,
            marginBottom: 28,
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: C.forest }} />
            <span style={{ fontSize: 12, color: C.forest, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase' }}>Powered by GNology</span>
          </div>

          <h1 style={{
            fontFamily: "'Playfair Display',serif", fontSize: 54, lineHeight: 1.1,
            color: C.charcoal, fontWeight: 700, margin: '0 0 24px 0',
          }}>
            Your Family History<br />Deserves a{' '}
            <em style={{ color: C.forest, fontStyle: 'italic' }}>Real</em>
            <br />Researcher
          </h1>

          <p style={{
            fontSize: 18, lineHeight: 1.7, color: C.charcoalLt,
            fontFamily: "'DM Sans',sans-serif", margin: '0 0 40px 0', maxWidth: 480,
          }}>
            Gene AI doesn't guess at your genealogy. She analyzes documents,
            cross-references records, and builds evidence-based family
            connections — the way a professional genealogist would.
          </p>

          <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
            <Link to="/register" style={{
              padding: '16px 36px', borderRadius: 8, background: C.forest,
              color: C.white, textDecoration: 'none', fontSize: 15, fontWeight: 700,
              fontFamily: "'DM Sans',sans-serif",
              boxShadow: '0 4px 24px rgba(46,125,50,0.2)',
              transition: 'all 0.3s ease',
            }}>Start Researching Free</Link>
            <a href="#how-it-works" style={{
              padding: '16px 36px', borderRadius: 8,
              border: `1.5px solid ${C.brownie}`, color: C.brownie,
              textDecoration: 'none', fontSize: 15, fontWeight: 600,
              fontFamily: "'DM Sans',sans-serif", transition: 'all 0.3s ease',
            }}>See How It Works</a>
          </div>
        </div>

        {/* Right: demo preview */}
        <div style={{
          opacity: visible ? 1 : 0,
          transform: visible ? 'translateY(0)' : 'translateY(40px)',
          transition: 'all 1s cubic-bezier(0.16,1,0.3,1) 0.2s',
        }}>
          <div style={{
            background: C.white, border: '1px solid rgba(139,69,19,0.12)',
            borderRadius: 16, boxShadow: '0 16px 60px rgba(54,52,47,0.08)',
            overflow: 'hidden',
          }}>
            {/* Browser bar */}
            <div style={{
              padding: '12px 16px', background: C.creamDk,
              borderBottom: '1px solid rgba(139,69,19,0.08)',
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {['#ff5f57','#ffbd2e','#28c840'].map(c => (
                  <div key={c} style={{ width: 10, height: 10, borderRadius: '50%', background: c, opacity: 0.6 }} />
                ))}
              </div>
              <div style={{ flex: 1, textAlign: 'center', fontSize: 11, color: C.charcoalPale, fontFamily: "'DM Sans',sans-serif" }}>mygnology.com/research</div>
            </div>

            {/* Demo conversation */}
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16, background: C.cream }}>
              {/* User msg */}
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{
                  background: 'rgba(139,69,19,0.08)', border: '1px solid rgba(139,69,19,0.15)',
                  borderRadius: '14px 14px 4px 14px', padding: '12px 18px', maxWidth: '80%',
                }}>
                  <p style={{ margin: 0, fontSize: 13, color: C.charcoal, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.5 }}>
                    I uploaded my great-grandmother's 1920 census record. Can you tell me about her household?
                  </p>
                </div>
              </div>
              {/* Gene msg */}
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                  background: `linear-gradient(135deg,${C.forest},${C.sage})`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700, color: C.white,
                  fontFamily: "'Playfair Display',serif",
                }}>G</div>
                <div>
                  <div style={{
                    background: C.white, border: '1px solid rgba(46,125,50,0.12)',
                    borderRadius: '4px 14px 14px 14px', padding: '14px 18px', maxWidth: '85%',
                  }}>
                    <p style={{ margin: 0, fontSize: 13, color: C.charcoal, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6 }}>
                      Based on the 1920 Federal Census for <strong style={{ color: C.forest }}>Hertford County, NC</strong>,
                      your great-grandmother Martha Joyner (age 42) was head of household with five children present.
                      I notice her husband William isn't listed — I'll cross-reference the 1910 census and
                      local death records to determine if he passed between 1910–1920.
                    </p>
                  </div>
                  <div style={{
                    marginTop: 10, padding: '8px 12px', background: C.forestPale,
                    borderRadius: 8, borderLeft: `2px solid ${C.forest}`,
                  }}>
                    <p style={{ margin: 0, fontSize: 11, color: C.forestDark, fontFamily: "'DM Sans',sans-serif" }}>
                      📋 Source: 1920 U.S. Federal Census, Hertford Co., NC, ED 45, Sheet 3A
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─── DIFFERENTIATOR ─────────────────────────────── */
const Differentiator = () => {
  const items = [
    { icon: '📄', bad: "ChatGPT says your ancestor 'might have' lived somewhere", good: 'Gene cites the exact census record, page, and line number' },
    { icon: '🔍', bad: 'Generic AI invents plausible-sounding family connections', good: "Gene says 'I don't have enough evidence' when the records aren't there" },
    { icon: '📚', bad: 'Other tools treat every document the same way', good: 'Gene recognizes census vs. vital records vs. DNA and adjusts analysis' },
  ];

  return (
    <section style={{ padding: '100px 24px', background: C.white }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 38, fontWeight: 700, color: C.charcoal, marginBottom: 16 }}>
              Not Another Chatbot.<br />A <em style={{ color: C.brownie }}>Research Partner</em>.
            </h2>
            <p style={{ fontSize: 16, color: C.charcoalLt, fontFamily: "'DM Sans',sans-serif", maxWidth: 560, margin: '0 auto', lineHeight: 1.7 }}>
              The difference between Gene and general AI is the difference
              between a librarian and someone who read a Wikipedia article.
            </p>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {items.map((item, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 48px 1fr', alignItems: 'stretch' }}>
                <div style={{ padding: '24px 28px', background: 'rgba(193,66,66,0.04)', border: '1px solid rgba(193,66,66,0.1)', borderRadius: '12px 0 0 12px' }}>
                  <span style={{ fontSize: 14, color: C.terra, fontWeight: 700, fontFamily: "'DM Sans',sans-serif" }}>✕ Generic AI</span>
                  <p style={{ marginTop: 8, fontSize: 14, color: C.charcoalLt, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6 }}>{item.bad}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: C.cream, fontSize: 20 }}>{item.icon}</div>
                <div style={{ padding: '24px 28px', background: C.forestPale, border: '1px solid rgba(46,125,50,0.12)', borderRadius: '0 12px 12px 0' }}>
                  <span style={{ fontSize: 14, color: C.forestDark, fontWeight: 700, fontFamily: "'DM Sans',sans-serif" }}>✓ Gene AI</span>
                  <p style={{ marginTop: 8, fontSize: 14, color: C.charcoal, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.6, fontWeight: 500 }}>{item.good}</p>
                </div>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

/* ─── HOW IT WORKS ───────────────────────────────── */
const HowItWorks = () => {
  const steps = [
    { num: '01', title: 'Upload Your Documents', desc: 'Census records, birth certificates, old photographs, DNA results, family letters — Gene reads and extracts data from any genealogical source.', detail: 'PDF, DOCX, images — all accepted' },
    { num: '02', title: 'Ask Your Questions', desc: "Ask in plain English. 'Who were my grandmother's siblings?' or 'What does this census record tell us?' Gene understands genealogical context.", detail: 'Natural conversation, not search queries' },
    { num: '03', title: 'Get Sourced Answers', desc: "Every answer comes with citations. Gene tells you what the evidence says, what's uncertain, and where to look next — never fabricates connections.", detail: 'Evidence-based, never speculative' },
    { num: '04', title: 'Build Your Family Story', desc: 'Turn your research into a polished family history book, complete with timelines, migration maps, and narrative chapters your family will treasure.', detail: 'From raw records to published legacy' },
  ];

  return (
    <section id="how-it-works" style={{ padding: '100px 24px', background: 'linear-gradient(180deg, #faf6f1 0%, #eef3e8 50%, #faf6f1 100%)' }}>
      <div style={{ maxWidth: 1000, margin: '0 auto' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 72 }}>
            <span style={{ fontSize: 12, color: C.forest, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>How It Works</span>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 38, fontWeight: 700, color: C.charcoal, marginTop: 16 }}>Four Steps to Your Family Story</h2>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 28 }}>
            {steps.map((step, i) => (
              <div key={i} style={{
                padding: 36, background: C.white,
                border: '1px solid rgba(135,169,107,0.2)', borderRadius: 16,
                position: 'relative', transition: 'all 0.3s ease',
              }}>
                <span style={{ fontFamily: "'Playfair Display',serif", fontSize: 48, fontWeight: 700, color: 'rgba(135,169,107,0.15)', position: 'absolute', top: 16, right: 24 }}>{step.num}</span>
                <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 22, fontWeight: 700, color: C.charcoal, marginBottom: 12 }}>{step.title}</h3>
                <p style={{ fontSize: 14, color: C.charcoalLt, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.7, marginBottom: 16 }}>{step.desc}</p>
                <div style={{ display: 'inline-flex', padding: '6px 14px', borderRadius: 100, background: C.forestPale, border: `1px solid ${C.forestLight}` }}>
                  <span style={{ fontSize: 11, color: C.forestDark, fontFamily: "'DM Sans',sans-serif", fontWeight: 600 }}>{step.detail}</span>
                </div>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

/* ─── FEATURES ───────────────────────────────────── */
const Features = () => {
  const features = [
    { icon: '🗂️', title: 'Document Analysis', desc: 'Upload census records, vital records, military documents, or photographs. Gene extracts names, dates, locations, and relationships automatically.' },
    { icon: '🗺️', title: 'Migration Mapping', desc: 'Visualize where your family lived across generations with interactive maps showing movement patterns over decades.' },
    { icon: '📖', title: 'Family Book Builder', desc: 'Turn your research into a professionally formatted family history book with chapters, timelines, and source citations.' },
    { icon: '🧬', title: 'DNA Interpretation', desc: 'Upload DNA match lists and let Gene help you understand genetic connections and identify potential family relationships.' },
    { icon: '🔎', title: 'Research Planning', desc: 'Gene creates personalized research plans, suggests which records to search next, and tracks your progress across family lines.' },
    { icon: '💬', title: 'Conversational Research', desc: 'No complicated forms. Just talk to Gene naturally about your family and she guides you through the research process step by step.' },
  ];

  return (
    <section id="features" style={{ padding: '100px 24px', background: C.cream }}>
      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 64 }}>
            <span style={{ fontSize: 12, color: C.brownie, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>Features</span>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 38, fontWeight: 700, color: C.charcoal, marginTop: 16 }}>Everything a Genealogist Needs</h2>
          </div>
        </FadeIn>
        <FadeIn delay={0.1}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }}>
            {features.map((f, i) => (
              <div key={i} style={{
                padding: 32, background: C.white,
                border: '1px solid rgba(139,69,19,0.08)', borderRadius: 16,
                transition: 'all 0.3s ease', cursor: 'default',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = C.forestLight; e.currentTarget.style.boxShadow = '0 8px 32px rgba(46,125,50,0.06)'; e.currentTarget.style.transform = 'translateY(-4px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(139,69,19,0.08)'; e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ fontSize: 28, marginBottom: 16, width: 52, height: 52, borderRadius: 12, background: C.forestPale, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{f.icon}</div>
                <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: 20, fontWeight: 700, color: C.charcoal, marginBottom: 10 }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: C.charcoalLt, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.7, margin: 0 }}>{f.desc}</p>
              </div>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

/* ─── CASE STUDY ─────────────────────────────────── */
const CaseStudy = () => {
  const timeline = [
    { year: '1820s', loc: 'Saint Johns Township, Hertford County, NC', event: 'Family established' },
    { year: '1880s', loc: 'Ahoskie, Hertford County, NC', event: 'Community expansion' },
    { year: '1910s', loc: 'Norfolk, VA', event: 'Great Migration begins' },
    { year: '1940s', loc: 'Philadelphia, PA', event: 'Northern settlement' },
    { year: 'Present', loc: 'Multiple states', event: 'Seven generations documented' },
  ];

  return (
    <section style={{ padding: '100px 24px', background: C.browniePale }}>
      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <FadeIn>
          <div style={{ textAlign: 'center', marginBottom: 48 }}>
            <span style={{ fontSize: 12, color: C.brownie, fontFamily: "'DM Sans',sans-serif", fontWeight: 600, letterSpacing: '0.15em', textTransform: 'uppercase' }}>What Gene Can Uncover</span>
            <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 38, fontWeight: 700, color: C.charcoal, marginTop: 16 }}>Seven Generations. One Story.</h2>
          </div>
        </FadeIn>

        <FadeIn delay={0.1}>
          <div style={{
            background: C.white, border: '1px solid rgba(139,69,19,0.12)',
            borderRadius: 20, padding: 48, boxShadow: '0 8px 40px rgba(139,69,19,0.06)',
          }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr', gap: 40 }}>
              {/* Left: quote + stats */}
              <div>
                <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 24, fontStyle: 'italic', color: C.brownieDk, lineHeight: 1.5, marginBottom: 24 }}>
                  "Gene traced our family from a single name on a faded census page to seven generations of connected history."
                </p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {[
                    { label: 'Generations traced', val: '7' },
                    { label: 'Counties documented', val: '4' },
                    { label: 'Records cross-referenced', val: '38' },
                    { label: 'Family book chapters', val: '12' },
                  ].map(s => (
                    <div key={s.label} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(139,69,19,0.08)' }}>
                      <span style={{ fontSize: 14, color: C.charcoalPale, fontFamily: "'DM Sans',sans-serif" }}>{s.label}</span>
                      <span style={{ fontSize: 20, color: C.forest, fontFamily: "'Playfair Display',serif", fontWeight: 700 }}>{s.val}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Divider */}
              <div style={{ background: 'linear-gradient(to bottom, transparent, rgba(139,69,19,0.15), transparent)' }} />

              {/* Right: timeline */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                <h4 style={{ fontSize: 13, fontWeight: 600, color: C.brownie, fontFamily: "'DM Sans',sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase', margin: 0 }}>Migration Timeline</h4>
                {timeline.map((item, i) => (
                  <div key={i} style={{ display: 'flex', gap: 16 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{
                        width: 10, height: 10, borderRadius: '50%',
                        background: i === 4 ? C.forest : C.forestLight,
                        border: `2px solid ${C.forest}`, flexShrink: 0,
                      }} />
                      {i < 4 && <div style={{ width: 1, flex: 1, background: 'rgba(46,125,50,0.2)' }} />}
                    </div>
                    <div style={{ paddingBottom: 4 }}>
                      <span style={{ fontSize: 13, color: C.forest, fontFamily: "'DM Sans',sans-serif", fontWeight: 700 }}>{item.year}</span>
                      <p style={{ margin: '2px 0 0', fontSize: 13, color: C.charcoalLt, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.5 }}>
                        {item.loc}<br /><em style={{ color: C.brownieLt }}>{item.event}</em>
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

/* ─── CTA ────────────────────────────────────────── */
const CTA = () => (
  <section style={{ padding: '100px 24px', background: C.creamDeep, textAlign: 'center' }}>
    <FadeIn>
      <div style={{ maxWidth: 640, margin: '0 auto' }}>
        <p style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontStyle: 'italic', color: C.brownie, marginBottom: 16 }}>
          "Every family tree has roots deeper than anyone can see."
        </p>
        <h2 style={{ fontFamily: "'Playfair Display',serif", fontSize: 42, fontWeight: 700, color: C.charcoal, marginBottom: 20, lineHeight: 1.2 }}>
          Start Discovering Your<br />Family's Full Story
        </h2>
        <p style={{ fontSize: 16, color: C.charcoalLt, fontFamily: "'DM Sans',sans-serif", lineHeight: 1.7, marginBottom: 36 }}>
          Your ancestors left a trail. Gene AI helps you follow it — with
          evidence, not guesswork. Begin your research today.
        </p>
        <Link to="/register" style={{
          display: 'inline-block', padding: '18px 48px', borderRadius: 8,
          background: C.forest, color: C.white, textDecoration: 'none',
          fontSize: 16, fontWeight: 700, fontFamily: "'DM Sans',sans-serif",
          boxShadow: '0 8px 32px rgba(46,125,50,0.2)',
          transition: 'all 0.3s ease',
        }}>Create Your Free Account</Link>
      </div>
    </FadeIn>
  </section>
);

/* ─── MAIN LANDING PAGE ──────────────────────────── */
const LandingPage = () => {
  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400;1,700&family=DM+Sans:wght@400;500;600;700&display=swap" rel="stylesheet" />
      <style>{`
        @media (max-width: 768px) {
          .landing-hero-grid { grid-template-columns: 1fr !important; gap: 32px !important; }
          .landing-hero-grid h1 { font-size: 36px !important; }
          .landing-hiw-grid { grid-template-columns: 1fr !important; }
          .landing-feat-grid { grid-template-columns: 1fr !important; }
          .landing-cs-grid { grid-template-columns: 1fr !important; }
          .landing-cs-divider { display: none !important; }
          .landing-cs-card { padding: 24px !important; }
          .landing-diff-row { grid-template-columns: 1fr !important; }
          .landing-diff-mid { display: none !important; }
          .landing-diff-bad, .landing-diff-good { border-radius: 12px !important; }
        }
      `}</style>
      <div style={{ marginTop: '-1rem' }}>
        <Hero />
        <Differentiator />
        <HowItWorks />
        <Features />
        <CaseStudy />
        <CTA />
      </div>
    </>
  );
};

export default LandingPage;
